import { t } from "@lingui/macro";
import {
  CommonActions,
  NavigationProp,
  useNavigation,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Image, Platform } from "react-native";
import { useFragment } from "react-relay";

import { buildStaffAppUrl } from "@/utils/getStaffAppUrlString";
import { ArrowRightIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Text,
  HStack,
  Pressable,
  ScrollView,
} from "@whoosh/design-system/src/components/primitive";
import { setStoredClubSlug } from "@whoosh/relay/src/clubSlugStorage";
import { STAFF_USER_CONTEXT } from "@whoosh/relay/src/constants";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";

import { BaseNavigatorParamList } from "../../../../navigators/BaseNavigator/types";
import { ClubSwitcherFragment_memberUser$key } from "./__generated__/ClubSwitcherFragment_memberUser.graphql";
import { ClubSwitcherFragment_session$key } from "./__generated__/ClubSwitcherFragment_session.graphql";

type ClubSwitcherProps = {
  isOpen: boolean;
  onClose: () => void;
  userRef: ClubSwitcherFragment_memberUser$key;
  sessionRef: ClubSwitcherFragment_session$key;
  currentClubSlug: string;
};

export const ClubSwitcher = ({
  isOpen,
  onClose,
  userRef,
  sessionRef,
  currentClubSlug,
}: ClubSwitcherProps) => {
  const session = useFragment(ClubSwitcherSessionFragmentString, sessionRef);
  const data = useFragment(ClubSwitcherFragmentString, userRef);

  const clubs = data?.memberUserProfiles?.edges?.flatMap((e) =>
    e?.node?.club ? [e.node.club] : []
  );

  const navigation = useNavigation<NavigationProp<BaseNavigatorParamList>>();

  const selectClub = (slug: string) => {
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: "Club", params: { clubSlug: slug } }],
      })
    );

    onClose();
  };

  return (
    <StandardModal
      upperTitle={t`Switch Club`}
      isOpen={isOpen}
      onClose={onClose}
      content={
        <ScrollView
          width="100%"
          pl={6}
          mt={2}
          showsVerticalScrollIndicator={false}
        >
          {Platform.OS === "web" && session.staffAccessAvailable ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t`Switch to Staff`}
              onPress={async () => {
                await setStoredClubSlug(STAFF_USER_CONTEXT);
                window.location.assign(buildStaffAppUrl(window.location));
              }}
            >
              <HStack
                justifyContent="space-between"
                alignItems="center"
                py={4}
                pr={6}
              >
                <Text variant="mainBaseMedium">{t`Staff`}</Text>
                <ArrowRightIcon width={16} height={16} color="grayscale-1000" />
              </HStack>
            </Pressable>
          ) : null}
          {clubs
            ? clubs
                .filter((c) => c?.slug !== currentClubSlug)
                .map((club) => {
                  return (
                    <Pressable
                      onPress={() => {
                        if (club?.slug) {
                          selectClub(club.slug);
                        }
                      }}
                      key={club?.id}
                      accessibilityRole="button"
                      accessibilityLabel={
                        club?.name ? t`Switch to ${club.name}` : t`Switch club`
                      }
                    >
                      <HStack
                        justifyContent="space-between"
                        pr={6}
                        alignItems="center"
                        borderBottomColor="grayscale-500"
                        borderBottomWidth={1}
                        style={{ paddingVertical: 15 }}
                      >
                        <HStack alignItems="center">
                          <Image
                            source={{
                              uri: getThumbnail(
                                club?.logoImage?.publicId,
                                55,
                                55
                              )?.toURL(),
                            }}
                            alt={t`Profile Image`}
                            style={{ width: 55, height: 55, borderRadius: 55 }}
                          />

                          <Text pl={4} variant="mainBaseMedium">
                            {club?.name}
                          </Text>
                        </HStack>
                        <ArrowRightIcon
                          accessible={false}
                          width={16}
                          height={16}
                          color="grayscale-1000"
                        />
                      </HStack>
                    </Pressable>
                  );
                })
            : null}
        </ScrollView>
      }
    />
  );
};

export const ClubSwitcherFragmentString = graphql`
  fragment ClubSwitcherFragment_memberUser on MemberUser {
    memberUserProfiles: memberProfiles(active: true, hidden: false) {
      edges {
        node {
          club {
            id
            slug
            name
            logoImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
        }
      }
    }
  }
`;

const ClubSwitcherSessionFragmentString = graphql`
  fragment ClubSwitcherFragment_session on Session {
    staffAccessAvailable
  }
`;
